import React from 'react';
import styled from '@emotion/styled';
import { theme } from '../../../styles/theme';

const StepIndicator = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing['2xl']};
  position: relative;
  padding: 0 ${theme.spacing.md};
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    margin-bottom: ${theme.spacing.xl};
    padding: 0;
  }

  &::after {
    content: '';
    position: absolute;
    top: 16px;
    left: 72px;
    right: 72px;
    height: 2px;
    background: var(--login-form-border);
    z-index: 0;

    @media (max-width: 768px) {
      left: 40px;
      right: 40px;
    }

    @media (max-width: 480px) {
      top: 14px;
      left: 28px;
      right: 28px;
    }
  }
`;

const Step = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  background: #ffffff;
  padding: 0 ${theme.spacing.sm};
  z-index: 1;
  position: relative;
  min-width: 0;

  @media (max-width: 768px) {
    padding: 0 ${theme.spacing.xs};
    gap: ${theme.spacing.xs};
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 6px;
    text-align: center;
    flex: 1;
  }
`;

const StepNumber = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  flex-shrink: 0;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  ${(props) =>
    props.$state === 'active' &&
    `
    background: var(--login-primary);
    color: #ffffff;
    border: 1px solid var(--login-primary);
  `}

  ${(props) =>
    props.$state === 'completed' &&
    `
    background: var(--login-primary);
    color: #ffffff;
    border: 1px solid var(--login-primary);
    font-size: 0.85rem;
  `}

  ${(props) =>
    props.$state === 'upcoming' &&
    `
    background: var(--login-form-input-bg);
    color: var(--login-form-muted);
    border: 1px solid var(--login-form-border);
  `}

  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    font-size: 0.8rem;
  }
`;

const StepLabel = styled.span`
  font-size: 0.95rem;
  line-height: 1.2;
  white-space: nowrap;

  ${(props) =>
    props.$state === 'active' &&
    `
    font-weight: 700;
    color: var(--login-form-text);
  `}

  ${(props) =>
    props.$state === 'completed' &&
    `
    font-weight: 600;
    color: var(--login-form-text);
  `}

  ${(props) =>
    props.$state === 'upcoming' &&
    `
    font-weight: 500;
    color: var(--login-form-muted);
  `}

  @media (max-width: 900px) {
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

const StepLabelMobile = styled.span`
  display: none;
  text-align: center;
  font-size: ${theme.typography.fontSize.xs};
  max-width: 72px;
  line-height: 1.25;
  word-wrap: break-word;

  ${(props) =>
    props.$state === 'active' &&
    `
    font-weight: 700;
    color: var(--login-form-text);
  `}

  ${(props) =>
    props.$state === 'completed' &&
    `
    font-weight: 600;
    color: var(--login-form-text);
  `}

  ${(props) =>
    props.$state === 'upcoming' &&
    `
    font-weight: 500;
    color: var(--login-form-muted);
  `}

  @media (max-width: 480px) {
    display: block;
  }
`;

const getStepState = (stepNumber, currentStep) => {
  if (stepNumber < currentStep) return 'completed';
  if (stepNumber === currentStep) return 'active';
  return 'upcoming';
};

const AccountStepper = ({ currentStep }) => {
  const steps = [
    { number: 1, label: 'Choose Your Package', mobileLabel: 'Package' },
    { number: 2, label: 'Business Information', mobileLabel: 'Business' },
    { number: 3, label: 'Review & Billing', mobileLabel: 'Review' },
  ];

  return (
    <StepIndicator aria-label="Account setup progress">
      {steps.map((step) => {
        const state = getStepState(step.number, currentStep);

        return (
          <Step key={step.number}>
            <StepNumber $state={state} aria-current={state === 'active' ? 'step' : undefined}>
              {state === 'completed' ? '✓' : step.number}
            </StepNumber>
            <StepLabel $state={state}>{step.label}</StepLabel>
            <StepLabelMobile $state={state}>{step.mobileLabel}</StepLabelMobile>
          </Step>
        );
      })}
    </StepIndicator>
  );
};

export default AccountStepper;
